import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSession } from "@/lib/auth-client";
import { PrincipalCard, TimeZoneBriefingFields } from "./cos/pieces";
import { usePreview } from "./preview-context";

// "Customize" disclosure stays hidden behind a flag — flip back to `true`
// to re-expose. Leaving the markup in place so re-enabling is a one-line
// change, not a re-add.
const SHOW_CUSTOMIZE = false;

// Boo naming is hidden too — every new Boo is just "Boo". Same
// flip-back-to-true convention as SHOW_CUSTOMIZE.
const SHOW_AGENT_NAME = false;

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// Users type bare domains ("acme.com") far more often than full URLs, so
// the input is type="text" and we add the scheme here instead of letting
// the browser's url-input validation reject scheme-less entries.
function normalizeWebsite(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    // Require a real-looking TLD (≥2 letters), not just any dot —
    // otherwise mid-typing fragments like "stripe.c" count as valid and
    // trigger junk suggestion fetches.
    if (!/\.[a-z]{2,}$/i.test(url.hostname)) return null;
    return url.href;
  } catch {
    return null;
  }
}

// Fallback org name when the suggestions call didn't produce one —
// "https://www.acme.com" → "Acme". The org must still be created even
// though the name field is gone from the form.
function orgNameFromWebsite(website: string): string {
  const host = new URL(website).hostname.replace(/^www\./, "");
  const label = host.split(".")[0] || host;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function OrganizationStep() {
  useDocumentTitle("Create organization");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    orgName,
    setOrgName,
    orgWebsite,
    setOrgWebsite,
    orgInfo,
    setOrgInfo,
    agentName,
    setAgentName,
    posture,
    timeZone,
    setTimeZone,
    briefingTime,
    setBriefingTime,
    suggestedWebsite,
  } = usePreview();
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const { data: session } = useSession();
  // Chief-of-staff branch: same org + container creation, plus the setup
  // inputs the brain's interview confirms instead of asking.
  const isCos = posture === "chief-of-staff";
  const lastFetchedWebsite = useRef<string | null>(null);

  const debouncedWebsite = useDebounce(orgWebsite, 500);

  const refetchSuggestions = useMutation({
    mutationFn: (website: string) => api.onboarding.suggestions(website),
    onSuccess: (data, website) => {
      // Concurrent fetches resolve out of order (a slow fetch for an
      // earlier URL can land after the current one) — only the response
      // for the most recently requested website may write.
      if (website !== lastFetchedWebsite.current) return;
      // Pre-fill the visible name + description fields from the fetch.
      // A new website replaces earlier values — the fields describe the
      // company behind the URL, not an accumulating draft.
      setOrgName(data.orgName || orgNameFromWebsite(website));
      setOrgInfo(data.orgInfo ?? "");
    },
    onError: (_err, website) => {
      if (website !== lastFetchedWebsite.current) return;
      // Suggestions are best-effort — still pre-fill a name so the
      // form isn't blocked on a failed fetch.
      setOrgName(orgNameFromWebsite(website));
    },
  });

  const { mutate: fetchSuggestions } = refetchSuggestions;
  useEffect(() => {
    const normalized = normalizeWebsite(debouncedWebsite);
    if (!normalized) return;
    if (normalized === lastFetchedWebsite.current) return;
    // Skip the initial (email-domain) suggested URL only on the very
    // first pass — its name + info are already applied, so re-fetching
    // returns the same thing. Once a different URL has been fetched
    // (lastFetchedWebsite is set), the visible name reflects that other
    // company, so returning to the suggested URL MUST re-fetch to
    // restore its name (BOO-308) — don't swallow it. Gating on
    // `lastFetchedWebsite.current === null` (nothing fetched yet) rather
    // than "first value seen" also keeps the pre-fill-then-edit case
    // working: a URL the user types before the pre-fill's debounce tick
    // won't match the suggested URL, so it still fetches.
    if (
      lastFetchedWebsite.current === null &&
      suggestedWebsite &&
      normalized === normalizeWebsite(suggestedWebsite)
    ) {
      lastFetchedWebsite.current = normalized;
      return;
    }
    lastFetchedWebsite.current = normalized;
    fetchSuggestions(normalized);
  }, [debouncedWebsite, suggestedWebsite, fetchSuggestions]);

  const submit = useMutation({
    mutationFn: async () => {
      const website = normalizeWebsite(orgWebsite);
      if (!website) {
        throw new Error("Enter your company website to continue.");
      }
      // The visible name field is pre-filled from the website fetch;
      // hostname fallback covers the field being cleared mid-submit.
      const finalOrgName = orgName.trim() || orgNameFromWebsite(website);
      // With the naming field hidden, ignore any suggested agentName —
      // every new Boo is plain "Boo".
      const finalAgentName = (SHOW_AGENT_NAME ? agentName.trim() : "") || "Boo";

      // 1. Set the active org. Auth-backend stopped auto-creating
      //    "personal" orgs at signup (PR #5), so a fresh signup has zero
      //    memberships and we create their first org here. If they
      //    happen to already have one (mid-onboarding re-entry: started
      //    onboarding, created an org, didn't finish), use the existing
      //    org instead of duplicating.
      const orgs = await api.orgs.list();
      const existing = orgs[0];
      const target = existing ?? (await api.orgs.create(finalOrgName, website));
      await api.orgs.setActive(target.id);

      // Persist the website as the org's canonical URL (BOO-274). New orgs
      // get it at create; a re-entry onto an existing org needs an update.
      // Best-effort — provisioning reads the URL from the container-create
      // body regardless, so don't block onboarding if this fails.
      if (existing && normalizeWebsite(existing.website ?? "") !== website) {
        await api.orgs.update(target.id, { website }).catch(() => {});
      }

      // 2. Lifecycle analytics — non-blocking, ignored if it fails.
      api.onboarding.orgStepComplete({
        orgName: finalOrgName,
        orgInfo: orgInfo || undefined,
        agentName: finalAgentName,
      }).catch(() => {});

      // 3. Kick off container creation. The backend inserts the row in
      //    the first ms of the handler then awaits a multi-minute
      //    provisioning chain. We don't await full completion — the
      //    Provisioning step polls /api/onboarding/state and surfaces
      //    sub-status from there. Race a 3s ceiling so the row is in
      //    the DB by the time we navigate; longer waits would just
      //    delay the user without adding signal.
      const createPromise = api.containers.create({
        name: finalAgentName,
        orgInfo: orgInfo || undefined,
        orgWebsite: website,
        ...(isCos
          ? { posture: "chief-of-staff" as const, setup: { timeZone, briefingTime } }
          : {}),
      }).catch(() => undefined);

      await Promise.race([
        createPromise,
        new Promise<void>((resolve) => setTimeout(resolve, 3000)),
      ]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session"] });
      queryClient.invalidateQueries({ queryKey: ["orgs"] });
      queryClient.invalidateQueries({ queryKey: ["onboarding-state"] });
      // Bounce through the onboarding index so GatedShell refetches
      // /api/onboarding/state and routes to the authoritative next step —
      // "provisioning" normally, or "waitlist" when the signup gate is on.
      navigate({ to: "/onboarding", replace: true });
    },
  });

  const websiteValid = normalizeWebsite(orgWebsite) !== null;

  const canSubmit =
    (!SHOW_AGENT_NAME || agentName.trim()) &&
    websiteValid &&
    orgName.trim() &&
    !submit.isPending &&
    !refetchSuggestions.isPending;

  const isRefetching = refetchSuggestions.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) submit.mutate();
      }}
      className="relative flex flex-col gap-6"
    >
      {isRefetching && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/80">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Fetching company info...
          </div>
        </div>
      )}

      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">
          {isCos ? "Where do you work?" : "Set up your workspace"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isCos
            ? "Boo learns the basics from your site."
            : "Share your company website and we'll set up your Boo."}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="org-website">Company website</Label>
        <Input
          id="org-website"
          type="text"
          inputMode="url"
          value={orgWebsite}
          onChange={(e) => setOrgWebsite(e.target.value)}
          disabled={isRefetching}
          autoFocus
          placeholder="acme.com"
          aria-invalid={orgWebsite.trim().length > 0 && !websiteValid ? true : undefined}
        />
        {orgWebsite.trim().length > 0 && !websiteValid && (
          <p className="text-xs text-destructive">Enter a valid website, like acme.com.</p>
        )}
      </div>

      {/* Company name, pre-filled from the website (suggestions fetch,
          hostname fallback) once a valid URL is in — editable so the user
          can correct what we inferred. The company description isn't
          collected here: Boo picks that up from the site during brand
          setup and its own work. */}
      {websiteValid && (
        <div className="space-y-1.5">
          <Label htmlFor="org-name">Company name</Label>
          <Input
            id="org-name"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            disabled={isRefetching}
            aria-invalid={orgName.length > 0 && !orgName.trim() ? true : undefined}
          />
        </div>
      )}

      {isCos && (
        <>
          <PrincipalCard name={session?.user.name ?? ""} email={session?.user.email ?? ""} />
          <TimeZoneBriefingFields
            timeZone={timeZone}
            briefingTime={briefingTime}
            onTimeZone={setTimeZone}
            onBriefingTime={setBriefingTime}
            disabled={isRefetching}
          />
        </>
      )}

      {SHOW_AGENT_NAME && (
        <div className="space-y-1.5">
          <Label htmlFor="agent-name">Name your Boo</Label>
          <Input
            id="agent-name"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            disabled={isRefetching}
            placeholder="Acme Boo"
          />
        </div>
      )}

      {SHOW_CUSTOMIZE && (
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => setCustomizeOpen((v) => !v)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronDown
            className={`size-3.5 transition-transform ${customizeOpen ? "rotate-0" : "-rotate-90"}`}
          />
          Customize
        </button>

        {customizeOpen && (
          <div className="space-y-3">
            {orgInfo && (
              <div className="space-y-1.5">
                <Label>About your organization</Label>
                <div className="max-h-28 overflow-y-auto rounded-md border border-border bg-muted/30 px-3 py-2">
                  <p className="text-xs text-muted-foreground whitespace-pre-line">{orgInfo}</p>
                </div>
              </div>
            )}

          </div>
        )}
      </div>
      )}

      {submit.isError && <InlineError error={submit.error} />}

      <Button
        type="submit"
        className="w-full mt-auto"
        disabled={!canSubmit}
        loading={submit.isPending}
      >
        Continue
      </Button>
    </form>
  );
}
